/**
 * /investor/dashboard — what an investor sees about themselves.
 *
 * Auth required, but no team membership needed. Backend scopes by
 * User.investor_id. If the logged-in user isn't linked to an investor
 * record, we show a soft-fail empty state.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Briefcase, Loader2, Sparkles, ArrowRight, Lightbulb, Send } from 'lucide-react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import DashboardLayout from '../components/DashboardLayout';
import ResearchSourcesPanel from '../components/ResearchSourcesPanel';
import selfServeService, {
  SelfServeInvestor, SelfServeMatch,
} from '../services/selfServeService';


const VERDICT_STYLE: Record<string, string> = {
  strong_fit: 'bg-green-100 text-green-700',
  good_fit: 'bg-emerald-50 text-emerald-700',
  marginal_fit: 'bg-yellow-50 text-yellow-700',
  poor_fit: 'bg-orange-50 text-orange-700',
  no_fit: 'bg-red-50 text-red-700',
};


const scoreColor = (s: number | null): string => {
  if (s === null || s === undefined) return 'bg-gray-100 text-gray-500';
  if (s >= 80) return 'bg-green-100 text-green-700';
  if (s >= 60) return 'bg-yellow-100 text-yellow-700';
  if (s >= 40) return 'bg-orange-100 text-orange-700';
  return 'bg-red-100 text-red-700';
};


const InvestorDashboardPage: React.FC = () => {
  const [investor, setInvestor] = useState<SelfServeInvestor | null>(null);
  const [matches, setMatches] = useState<SelfServeMatch[]>([]);
  const [loading, setLoading] = useState(true);
  const [unlinked, setUnlinked] = useState(false);
  const [feedbackText, setFeedbackText] = useState('');
  const [feedbackSentiment, setFeedbackSentiment] = useState<
    'agree' | 'more_like_this' | 'fewer_like_this' | null
  >(null);
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);

  // Compute a one-line "Why these matches" summary from the investor's
  // own profile fields. If the investor's stage/sector/check-size are
  // populated, the matching engine is using them; if they're missing,
  // surface that as a refinement opportunity rather than mystery.
  const insights = useMemo(() => {
    if (!investor) return null;
    const stages = investor.stage_preferences?.length ? investor.stage_preferences.join(', ') : null;
    const sectors = investor.sector_focus?.length ? investor.sector_focus.slice(0, 4).join(', ') : null;
    const checkBand =
      investor.check_size_min !== null && investor.check_size_max !== null
        ? `$${(investor.check_size_min / 1000).toLocaleString()}K – $${(investor.check_size_max / 1000).toLocaleString()}K`
        : null;
    const hasAny = stages || sectors || checkBand;
    return {
      stages, sectors, checkBand, hasAny,
      matchCount: matches.length,
      avgScore: matches.length > 0
        ? Math.round(matches.reduce((sum, m) => sum + (m.overall_score ?? 0), 0) / matches.length)
        : null,
    };
  }, [investor, matches]);

  const submitFeedback = async () => {
    if (!feedbackText.trim()) {
      toast.error('Add a short note before submitting');
      return;
    }
    setFeedbackSubmitting(true);
    const r = await selfServeService.addMyInvestorMatchFeedback({
      note: feedbackText.trim(),
      sentiment: feedbackSentiment ?? undefined,
    });
    setFeedbackSubmitting(false);
    if (r.error) {
      toast.error(r.error);
      return;
    }
    toast.success('Thanks — your feedback will refine future matches.');
    setFeedbackText('');
    setFeedbackSentiment(null);
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      const profileRes = await selfServeService.getMyInvestor();
      if (profileRes.error) {
        if (profileRes.error.toLowerCase().includes('not linked')) {
          setUnlinked(true);
        } else {
          toast.error(profileRes.error);
        }
      } else if (profileRes.data) {
        setInvestor(profileRes.data);
      }
      const matchRes = await selfServeService.listMyInvestorMatches();
      if (matchRes.data) setMatches(matchRes.data);
      setLoading(false);
    })();
  }, []);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-20 text-gray-500">
          <Loader2 className="w-5 h-5 animate-spin mr-2" />
          Loading…
        </div>
      </DashboardLayout>
    );
  }

  if (unlinked) {
    return (
      <DashboardLayout>
        <div className="max-w-xl mx-auto text-center py-20 px-4">
          <Briefcase className="w-12 h-12 text-gray-400 mx-auto" />
          <h1 className="text-xl font-semibold mt-4">No investor profile linked</h1>
          <p className="text-gray-600 mt-2 leading-relaxed">
            Your account isn't connected to an investor record yet. Ask the
            person who invited you to the platform to link your account, or
            sign in with the email address that's already on your investor
            profile.
          </p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-5xl mx-auto px-4 py-8 space-y-6">
        <div>
          <h1 className="text-3xl font-semibold flex items-center gap-2">
            <Briefcase className="w-7 h-7 text-indigo-500" />
            {investor?.firm_name || investor?.full_name || 'Your investor profile'}
          </h1>
          <p className="text-gray-600 mt-1">
            {matches.length} match{matches.length === 1 ? '' : 'es'} curated for you
          </p>
        </div>

        {investor && (
          <div className="bg-white border border-gray-200 rounded-lg p-5 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">Stage focus</div>
              <div className="mt-1 text-sm">
                {investor.stage_preferences?.join(', ') || '—'}
              </div>
            </div>
            <div>
              <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">Sectors</div>
              <div className="mt-1 text-sm">
                {investor.sector_focus?.join(', ') || '—'}
              </div>
            </div>
            <div>
              <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">Check size</div>
              <div className="mt-1 text-sm">
                {investor.check_size_min !== null && investor.check_size_max !== null
                  ? `$${(investor.check_size_min / 1000).toLocaleString()}K – $${(investor.check_size_max / 1000).toLocaleString()}K`
                  : '—'}
              </div>
            </div>
          </div>
        )}

        {/* Sources used to build your profile */}
        {investor && (
          <ResearchSourcesPanel entityKind="investor" entityId={investor.id} />
        )}

        {/* Insights summary — explains the basis of the curated list. */}
        {insights && insights.hasAny && (
          <section className="bg-blue-50 border border-blue-200 rounded-lg p-5">
            <div className="flex items-start gap-3">
              <Lightbulb className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="min-w-0 flex-1">
                <h3 className="text-sm font-semibold text-blue-900">Why these matches?</h3>
                <p className="text-sm text-blue-900 mt-1 leading-relaxed">
                  {insights.matchCount > 0 ? (
                    <>
                      We surfaced these <strong>{insights.matchCount}</strong> companies
                      {insights.avgScore !== null && (
                        <> (avg score <strong>{insights.avgScore}</strong>)</>
                      )}{' '}
                      because they best fit your{' '}
                      {insights.stages && (<><strong>stage focus</strong> ({insights.stages})</>)}
                      {insights.stages && (insights.sectors || insights.checkBand) ? ', ' : ''}
                      {insights.sectors && (<><strong>sectors</strong> ({insights.sectors})</>)}
                      {insights.sectors && insights.checkBand ? ', ' : ''}
                      {insights.checkBand && (<><strong>check size</strong> ({insights.checkBand})</>)}
                      .
                    </>
                  ) : (
                    <>
                      We have your{' '}
                      {insights.stages && <>stage focus, </>}
                      {insights.sectors && <>sectors, </>}
                      {insights.checkBand && <>check size, </>}
                      but no companies match yet. Add a note below to tell us
                      what you're hunting for and we'll prioritize accordingly.
                    </>
                  )}
                </p>
                <p className="text-xs text-blue-800 mt-2">
                  Want different matches?  Use the feedback box below — we update
                  your profile and re-score on the next pass.
                </p>
              </div>
            </div>
          </section>
        )}

        {/* Investor feedback capture — free-form refinement note +
            optional sentiment chip. Persists on investor.notes; the
            matching engine reads notes when it re-scores. */}
        {investor && (
          <section className="bg-white border border-gray-200 rounded-lg p-5">
            <div className="flex items-start justify-between gap-3 mb-3">
              <div>
                <h3 className="text-sm font-semibold text-gray-900">
                  Refine your matches
                </h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  "I'm looking for X" or "I want fewer matches like Y" — your
                  note becomes part of the matching engine's input on the next
                  re-score pass.
                </p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2 mb-3">
              {([
                { v: 'agree', label: '👍 These look right', cls: 'border-emerald-300 text-emerald-700 bg-emerald-50' },
                { v: 'more_like_this', label: '✦ More like these', cls: 'border-blue-300 text-blue-700 bg-blue-50' },
                { v: 'fewer_like_this', label: '✗ Fewer like these', cls: 'border-amber-300 text-amber-700 bg-amber-50' },
              ] as const).map((o) => {
                const active = feedbackSentiment === o.v;
                return (
                  <button
                    key={o.v}
                    type="button"
                    onClick={() => setFeedbackSentiment(active ? null : o.v)}
                    className={`px-3 py-1 text-xs font-medium rounded-full border ${
                      active ? `${o.cls} ring-1 ring-offset-0` : 'border-gray-300 text-gray-600 bg-white hover:bg-gray-50'
                    }`}
                  >
                    {o.label}
                  </button>
                );
              })}
            </div>
            <textarea
              rows={3}
              value={feedbackText}
              onChange={(e) => setFeedbackText(e.target.value)}
              placeholder="e.g. I'd like more pre-seed B2B SaaS founders with technical co-founders. Skip anything that hasn't shipped a product."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
              disabled={feedbackSubmitting}
            />
            <div className="flex justify-end mt-3">
              <button
                onClick={submitFeedback}
                disabled={feedbackSubmitting || !feedbackText.trim()}
                className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg disabled:opacity-50"
              >
                {feedbackSubmitting ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Send className="w-4 h-4 mr-2" />
                )}
                Submit feedback
              </button>
            </div>
          </section>
        )}

        <section>
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
            Your matches
          </h2>
          {matches.length === 0 ? (
            <div className="bg-white border border-gray-200 rounded-lg p-8 text-center text-gray-500">
              No matches yet. Once new companies hit the system you'll see them here.
            </div>
          ) : (
            <div className="bg-white border border-gray-200 rounded-lg divide-y divide-gray-200">
              {matches.map((m) => (
                <Link
                  key={m.id}
                  to={`/investor/matches/${m.id}`}
                  className="flex items-center gap-4 p-4 hover:bg-gray-50"
                >
                  <div className={`flex-shrink-0 w-14 h-14 rounded-lg flex items-center justify-center font-semibold ${scoreColor(m.overall_score)}`}>
                    {m.overall_score ?? '—'}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-medium truncate">{m.counterparty_name || '(unknown)'}</div>
                    <div className="text-sm text-gray-500 mt-0.5 truncate">
                      {m.ai_review_headline || m.recommended_instrument || ' '}
                    </div>
                    <div className="flex items-center gap-2 mt-1.5 text-xs">
                      {m.ai_review_verdict && (
                        <span className={`px-1.5 py-0.5 rounded ${VERDICT_STYLE[m.ai_review_verdict] || 'bg-gray-100 text-gray-600'}`}>
                          <Sparkles className="inline w-3 h-3 mr-0.5" />
                          {m.ai_review_verdict.replace('_', ' ')}
                        </span>
                      )}
                      {m.status && (
                        <span className="text-gray-500">· {m.status.replace('_', ' ')}</span>
                      )}
                    </div>
                  </div>
                  <ArrowRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
                </Link>
              ))}
            </div>
          )}
        </section>
      </div>
    </DashboardLayout>
  );
};

export default InvestorDashboardPage;
